import { Controller } from '@hotwired/stimulus';
import { Carousel, Modal } from 'bootstrap';

export default class extends Controller {

    static values = {
        uploadPhotosUrl: String,
        deletePhotoUrl: String,
    };

    declare readonly uploadPhotosUrlValue: string;
    declare readonly deletePhotoUrlValue: string;

    async uploadPhotos(e: Event): Promise<void> {
        const input = e.currentTarget as HTMLInputElement;
        const visitId = input.dataset.visitIdForUpload;
        if (!visitId || !input.files?.length) return;

        await this.doUploadPhotos(visitId, input.files);
        input.value = '';
    }

    private async doUploadPhotos(visitId: string, files: FileList): Promise<void> {
        const formData = new FormData();
        Array.from(files).forEach((file) => formData.append('photos[]', file));

        const url = this.uploadPhotosUrlValue.replace('/0/', '/' + visitId + '/');
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
        });
        const json = await response.json();
        if (!json.success) return;

        const rowContainer = this.element.querySelector(`[data-visit-photos-container="${visitId}"]`);
        if (!rowContainer) return;

        json.photos.forEach((photo: { id: number; url: string }) => {
            const thumb = this.buildPhotoThumb(photo.id, visitId, photo.url);
            const label = rowContainer.lastElementChild as HTMLElement | null;
            rowContainer.insertBefore(thumb, label);
        });
    }

    async deletePhoto(e: Event): Promise<void> {
        e.preventDefault();
        const btn = e.currentTarget as HTMLElement;
        const photoId = btn.dataset.photoId;
        if (!photoId) return;

        const url = this.deletePhotoUrlValue.replace('/0', '/' + photoId);
        const response = await fetch(url, {
            method: 'DELETE',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const json = await response.json();
        if (!json.success) return;

        this.element.querySelectorAll(`[data-photo-id="${photoId}"]`).forEach((button) => {
            button.closest('.position-relative')?.remove();
        });
    }

    openPhotoPreview(e: Event): void {
        const img = e.currentTarget as HTMLImageElement;
        const clickedSrc = img.dataset.src ?? img.src;

        // Find all photos in the same visit row
        const container = img.closest('[data-visit-photos-container]');
        if (!container) return;

        const allImages = container.querySelectorAll<HTMLImageElement>('img[data-src]');
        const photos: string[] = [];
        let activeIndex = 0;

        allImages.forEach((photo, index) => {
            const src = photo.dataset.src ?? photo.src;
            photos.push(src);
            if (src === clickedSrc) activeIndex = index;
        });

        // Build carousel items
        const carouselInner = document.getElementById('visitPhotoCarouselInner');
        const counter = document.getElementById('visitPhotoCounter');
        if (!carouselInner) return;

        carouselInner.innerHTML = photos.map((src, i) =>
            `<div class="carousel-item${i === activeIndex ? ' active' : ''}">
                <img src="${this.esc(src)}" class="d-block mx-auto img-fluid rounded" style="max-height:80vh;">
            </div>`
        ).join('');

        if (counter) {
            counter.textContent = photos.length > 1 ? `${activeIndex + 1} / ${photos.length}` : '';
        }

        // Update counter on slide
        const carouselEl = document.getElementById('visitPhotoCarousel');
        if (carouselEl && photos.length > 1) {
            carouselEl.addEventListener('slid.bs.carousel', (event: any) => {
                if (counter) counter.textContent = `${event.to + 1} / ${photos.length}`;
            });
        }

        // Show/hide controls based on photo count
        const controls = carouselEl?.querySelectorAll('.carousel-control-prev, .carousel-control-next');
        controls?.forEach(ctrl => {
            (ctrl as HTMLElement).style.display = photos.length > 1 ? '' : 'none';
        });

        const modalEl = document.getElementById('visitPhotoPreviewModal');
        if (modalEl) {
            Modal.getOrCreateInstance(modalEl).show();
        }
    }

    private buildPhotoThumb(id: number, visitId: string, src: string): HTMLElement {
        const div = document.createElement('div');
        div.className = 'position-relative';
        div.style.cssText = 'width:48px;height:48px;';
        div.innerHTML = `
            <img src="${this.esc(src)}" class="rounded object-fit-cover border"
                 style="width:48px;height:48px;cursor:pointer;"
                 data-action="click->visit#openPhotoPreview"
                 data-src="${this.esc(src)}">
            <button type="button"
                    class="btn btn-danger btn-sm position-absolute top-0 end-0 p-0 lh-1"
                    style="width:16px;height:16px;font-size:10px;"
                    data-photo-id="${id}"
                    data-action="visit#deletePhoto">×</button>
        `;

        return div;
    }

    private esc(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}
